import {
    CarIcon,
    CheckIcon,
    DollarSignIcon,
    PlusIcon,
    RabbitIcon,
    XIcon,
} from 'lucide-react-native';
import { useState } from 'react';
import { XStack, YStack } from 'tamagui';

import {
    CheckboxRowInsetList,
    CheckboxRowList,
    LegacyCheckboxRow,
} from '../../components/inputs/Checkbox';
import { Input } from '../../components/inputs/Input';
import { SegmentedControl } from '../../components/segmented-controls/SegmentedControl';
import { SelectMultiple } from '../../components/select/SelectMultiple';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
} from '../../components/text/Typography';

export const SelectMultipleExample = () => {
    const [selectedOptions, setSelectedOptions] = useState<Set<number>>(
        new Set(),
    );

    const toggleOption = (optionId: number) => {
        const newSelected = new Set(selectedOptions);
        if (newSelected.has(optionId)) {
            newSelected.delete(optionId);
        } else {
            newSelected.add(optionId);
        }
        setSelectedOptions(newSelected);
    };

    const options = [
        {
            title: 'Default state',
            icon: DollarSignIcon,
        },
        {
            title: 'Without icon',
        },
        {
            title: 'With different icon',
            icon: PlusIcon,
        },
        {
            title: 'Selected state',
            icon: CheckIcon,
        },
        {
            title: 'Disabled state',
            icon: XIcon,
            disabled: true,
        },
    ];

    return (
        <YStack gap={80}>
            <YStack gap={16}>
                <BodyLargeEmphasized>SelectMultiple</BodyLargeEmphasized>
                <BodySmallRegular>
                    A select component for multiple selection scenarios.
                    Features an icon on the left and title text, with three
                    states: default, selected, and disabled. Shrinks to content
                    size and perfect for multi-select lists.
                </BodySmallRegular>
                <YStack maxWidth={450}>
                    <SelectMultiple
                        options={options}
                        selectedIndices={Array.from(selectedOptions)}
                        onSelect={toggleOption}
                    />
                </YStack>
            </YStack>
            <CheckboxRowListExamples />
        </YStack>
    );
};

const CheckboxRowListExamples = () => {
    const [disabled, setDisabled] = useState(false);
    const [checkedStates, setCheckedStates] = useState([
        0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 0, 0,
    ]);
    const toggleCheckboxState = (index: number) => {
        setCheckedStates((prev) => {
            const newCheckedStates = [...prev];
            newCheckedStates[index] = newCheckedStates[index] === 0 ? 1 : 0;
            return newCheckedStates;
        });
    };

    const [selectedMovies, setSelectedMovies] = useState<string[]>([]);

    return (
        <>
            <YStack gap={16}>
                <BodyLargeEmphasized>CheckboxRow</BodyLargeEmphasized>
                <SegmentedControl
                    selected={disabled ? 'true' : 'false'}
                    defaultValue={disabled ? 'true' : 'false'}
                    onValueChange={(v) => {
                        setDisabled(v === 'true');
                    }}
                    options={[
                        { label: 'Enabled', value: 'false' },
                        { label: 'Disabled', value: 'true' },
                    ]}
                    size="small"
                    width="auto"
                />
                <YStack gap={12} maxWidth={400}>
                    <LegacyCheckboxRow
                        label="CheckboxRow plain"
                        id="cb12a"
                        isSelected={checkedStates[0] === 1}
                        onPress={() => {
                            toggleCheckboxState(0);
                        }}
                        disabled={disabled}
                    />
                    <LegacyCheckboxRow
                        id="cb13"
                        label="CheckboxRow with icon"
                        labelIcon={CarIcon}
                        isSelected={checkedStates[1] === 1}
                        onPress={() => {
                            toggleCheckboxState(1);
                        }}
                        disabled={disabled}
                    />
                    <LegacyCheckboxRow
                        id="cb15a"
                        label="CheckboxRow"
                        details="With detail text here"
                        isSelected={checkedStates[2] === 1}
                        onPress={() => {
                            toggleCheckboxState(2);
                        }}
                        disabled={disabled}
                    />
                    <LegacyCheckboxRow
                        id="cb15c"
                        label="CheckboxRow with leftAlignOnWideScreen"
                        isSelected={checkedStates[3] === 1}
                        onPress={() => {
                            toggleCheckboxState(3);
                        }}
                        leftAlignOnWideScreen
                        disabled={disabled}
                    />
                    <LegacyCheckboxRow
                        id="cb16"
                        label="CheckboxRow - select to see details"
                        labelIcon={RabbitIcon}
                        detailsHiddenUntilSelected={true}
                        details={
                            <YStack flex={1}>
                                <BodySmallRegular>
                                    detailsHiddenUntilSelected = true
                                </BodySmallRegular>
                                <Input
                                    marginTop={12}
                                    placeholder="What is your favorite color?"
                                    enterKeyHint="done"
                                    disabled={disabled}
                                />
                            </YStack>
                        }
                        isSelected={checkedStates[4] === 1}
                        onPress={() => {
                            toggleCheckboxState(4);
                        }}
                        disabled={disabled}
                    />
                    <LegacyCheckboxRow
                        id="cb17"
                        label="CheckboxRow - custom details always shown"
                        details={
                            <Input
                                value="20"
                                marginTop={12}
                                placeholder="How neat is that?"
                                enterKeyHint="done"
                                disabled={disabled || checkedStates[5] !== 1}
                            />
                        }
                        isSelected={checkedStates[5] === 1}
                        onPress={() => {
                            toggleCheckboxState(5);
                        }}
                        disabled={disabled}
                    />
                </YStack>
            </YStack>
            <YStack gap={16}>
                <BodyLargeEmphasized>CheckboxRowList</BodyLargeEmphasized>
                <YStack
                    gap={12}
                    $md={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <XStack width="100%" maxWidth={400}>
                        <CheckboxRowList
                            items={[
                                'The Fellowship of the Ring',
                                'Two Towers',
                                'Return of the King',
                            ].map((value) => ({
                                id: value,
                                label: value,
                                isSelected:
                                    selectedMovies?.includes(value) ?? false,
                                onPress: () => {
                                    setSelectedMovies((prev) => {
                                        return prev.includes(value)
                                            ? prev.filter((v) => v !== value)
                                            : [...prev, value];
                                    });
                                },
                                disabled,
                            }))}
                        />
                    </XStack>
                </YStack>
            </YStack>
            <YStack gap={16}>
                <BodyLargeEmphasized>CheckboxRowInsetList</BodyLargeEmphasized>
                <YStack
                    gap={12}
                    $md={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <XStack width="100%" maxWidth={400}>
                        <CheckboxRowInsetList
                            title="Which movies do you like?"
                            items={[
                                'The Matrix',
                                'The Matrix: Reloaded',
                                'The Matrix: Revolutions',
                            ].map((value) => ({
                                id: value,
                                label: value,
                                isSelected:
                                    selectedMovies?.includes(value) ?? false,
                                onPress: () => {
                                    setSelectedMovies((prev) => {
                                        return prev.includes(value)
                                            ? prev.filter((v) => v !== value)
                                            : [...prev, value];
                                    });
                                },
                                disabled,
                            }))}
                        />
                    </XStack>
                </YStack>
            </YStack>
        </>
    );
};
